import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('i18nConfig');

// Keep fallback language in the main bundle and lazy-load all other locales.
import enTranslations from './locales/en.json';

const SUPPORTED_LANGUAGES = ['en', 'de', 'nl', 'fr', 'it', 'es'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const localeLoaders: Record<SupportedLanguage, () => Promise<{ default: unknown }>> = {
  en: async () => ({ default: enTranslations }),
  de: () => import('./locales/de.json'),
  nl: () => import('./locales/nl.json'),
  fr: () => import('./locales/fr.json'),
  it: () => import('./locales/it.json'),
  es: () => import('./locales/es.json'),
};

function normalizeLanguage(language?: string): SupportedLanguage {
  const languageOnly = (language || 'en').toLowerCase().split('-')[0] as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(languageOnly) ? languageOnly : 'en';
}

const resources = {
  en: {
    translation: enTranslations,
  },
} as const;

function isI18nDebugEnabled(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem('vital_i18n_debug') === '1';
  } catch {
    return false;
  }
}

const I18N_DEBUG_ENABLED = isI18nDebugEnabled();

// Log what we're loading only when explicitly enabled in dev.
if (I18N_DEBUG_ENABLED) {
  logger.info('Loading languages', {
    bundledLanguages: Object.keys(resources),
    lazyLoadedLanguages: SUPPORTED_LANGUAGES.filter((language) => language !== 'en'),
  });
}

export const i18nReady = i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend(async (language: string) => {
      const normalizedLanguage = normalizeLanguage(language);
      return localeLoaders[normalizedLanguage]();
    }),
  )
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    partialBundledLanguages: true,
    preload: ['en'],
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    lowerCaseLng: true,
    cleanCode: true,
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    // Trigger HMR update
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    debug: I18N_DEBUG_ENABLED,
  });

// Log initialization result only when explicitly enabled in dev.
if (I18N_DEBUG_ENABLED) {
  i18n.on('initialized', () => {
    logger.info('i18n Initialized', {
      languagesLoaded: Object.keys(i18n.store.data),
      currentLanguage: i18n.language,
      fallbackTranslation: i18n.t('anamnesebogen.title', { lng: 'en' }),
    });
  });

  i18n.on('languageChanged', (lng) => {
    logger.info('Language changed', {
      language: lng,
      testTranslation: i18n.t('anamnesebogen.title'),
    });
  });
}

export default i18n;
